/**
 * Iter-235j — Shift Metrics Wizard (Campaign Intelligence Phase 1)
 *
 * Mobile-first 5-step wizard:
 *   1. Role confirm  (auto-inferred from shift, talent can change)
 *   2. Metrics       (role-conditional numeric fields, AI-pre-filled when voice exists)
 *   3. Highlights    (required: biggest success, most common Q, objection, competitor, recs, observations)
 *   4. Photos        (up to 10 × 10MB)
 *   5. Review + Submit
 *
 * AI-prefilled fields show a yellow "AI suggested · verify" badge.
 *
 * The component is dependency-free of the clock-out flow — it accepts
 * a `booking` prop and is opened by whoever wants it. Phase 1 only
 * lets us preview the UI; Phase 1B wires it after the Voice Recap
 * modal in ClockInOut.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import { downscaleImage } from '../utils/imageDownscale';
import { enqueueUpload, isNetworkError } from '../utils/offlineUploadQueue';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import {
  ArrowLeft, ArrowRight, Loader2, Sparkles, Check, CheckCircle2,
  Mic, Camera, Megaphone, Users, Star,
} from 'lucide-react';
import { toast } from 'sonner';

const FIELD_LABELS = {
  samples_distributed:      'Samples distributed',
  consumer_engagements:     'Consumer engagements / conversations',
  product_demos:            'Product demonstrations',
  leads_collected:          'Leads collected',
  qr_scans:                 'QR scans',
  email_signups:            'Email signups',
  coupon_redemptions:       'Coupon redemptions',
  retail_sales_cents:       'Retail sales ($)',
  booth_traffic:            'Booth traffic (est.)',
  content_pieces_delivered: 'Content pieces delivered',
  posts_created:            'Posts created',
  reels_created:            'Reels created',
  stories_created:          'Stories created',
};

const HIGHLIGHT_LABELS = {
  biggest_success:         'Biggest success of the event',
  most_common_question:    'Most common consumer question',
  most_common_objection:   'Most common objection',
  competitor_observations: 'Competitor observations',
  recommendations:         'Recommendations for future events',
  additional_observations: 'Additional observations',
};

const STEPS = [
  { id: 'role',       label: 'Role',       icon: Users },
  { id: 'metrics',    label: 'Metrics',    icon: Megaphone },
  { id: 'highlights', label: 'Highlights', icon: Star },
  { id: 'photos',     label: 'Photos',     icon: Camera },
  { id: 'review',     label: 'Review',     icon: CheckCircle2 },
];

const ShiftMetricsWizard = ({ bookingId, onClose, onSubmitted, autoExtractFromVoice = true }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [booking, setBooking] = useState(null);
  const [template, setTemplate] = useState(null);
  const [trackRetail, setTrackRetail] = useState(false);
  const [form, setForm] = useState({});
  const [aiPrefilled, setAiPrefilled] = useState({});  // {field: true}

  // ── Load on mount ───────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const meta = await api.get(`/shift-metrics/${bookingId}`);
        if (cancelled) return;
        setBooking(meta.data.booking);
        setTrackRetail(!!meta.data.track_retail_sales);
        const initialRole = meta.data.metrics?.role_category || meta.data.suggested_role || 'event_staff';
        const tpl = await api.get(`/shift-metrics/template?role=${initialRole}`);
        if (cancelled) return;
        setTemplate(tpl.data);
        // Seed form from existing draft if any
        const m = meta.data.metrics || {};
        setForm({
          role_category: initialRole,
          ...Object.fromEntries(Object.entries(m).filter(([k, v]) => v !== null && v !== undefined)),
        });
        // Auto-extract from voice if we have a transcript and no AI extraction yet
        if (autoExtractFromVoice && !m.ai_extracted_from_voice) {
          runAiExtraction();
        }
      } catch (err) {
        toast.error('Failed to load shift metrics', { description: err?.response?.data?.detail || err.message });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  // ── AI extraction kicker ────────────────────────────────────────
  const runAiExtraction = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/shift-metrics/${bookingId}/extract-from-voice`);
      const ext = r.data.metrics || {};
      // Mark every applied numeric / highlight field as AI-suggested
      const applied = r.data.applied_fields || [];
      const flagged = {};
      applied.forEach((k) => { flagged[k] = true; });
      setAiPrefilled((p) => ({ ...p, ...flagged }));
      // Merge AI-extracted values into the form (don't overwrite user edits)
      setForm((prev) => {
        const next = { ...prev };
        applied.forEach((k) => {
          if (next[k] === undefined || next[k] === null || next[k] === '') {
            next[k] = ext[k];
          }
        });
        return next;
      });
      toast.success('Voice recap parsed — verify the highlighted fields', { duration: 4000 });
    } catch (err) {
      // Soft fail — no voice or extraction failed
      console.info('[wizard] AI extraction skipped:', err?.response?.data?.detail || err.message);
    } finally {
      setAiBusy(false);
    }
  };

  // ── Form helpers ─────────────────────────────────────────────────
  const setField = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    // If the user edits an AI-flagged field, the badge stays (they've reviewed it).
  };

  const numericFields = useMemo(() => {
    if (!template) return [];
    const fields = [...(template.numeric_fields || [])];
    if (trackRetail) fields.push('retail_sales_cents');
    return fields;
  }, [template, trackRetail]);

  const changeRole = async (newRole) => {
    setField('role_category', newRole);
    try {
      const tpl = await api.get(`/shift-metrics/template?role=${newRole}`);
      setTemplate(tpl.data);
    } catch (err) {
      toast.error('Could not load fields for that role');
    }
  };

  // ── Persist ─────────────────────────────────────────────────────
  const persistDraft = async () => {
    setSaving(true);
    try {
      await api.post(`/shift-metrics/${bookingId}/draft`, form);
    } catch (err) {
      toast.error('Draft save failed', { description: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  const submitFinal = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/shift-metrics/${bookingId}/submit`, form);
      toast.success('Campaign data recorded — your client will see this');
      onSubmitted?.(r.data);
    } catch (err) {
      toast.error('Submit failed', { description: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  // ── Navigation ──────────────────────────────────────────────────
  const next = async () => {
    // Autosave between steps so we don't lose data if the user navigates away.
    await persistDraft();
    setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  };
  const prev = () => setStepIdx((i) => Math.max(0, i - 1));

  // ── Render ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="wizard-loading">
        <Loader2 className="w-7 h-7 animate-spin text-cyan-500" />
      </div>
    );
  }

  const Step = STEPS[stepIdx];

  return (
    <div className="max-w-2xl mx-auto" data-testid="shift-metrics-wizard">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900">Shift Recap</h2>
        <p className="text-sm text-gray-500">
          {booking?.shift_title || 'Your assignment'}
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-between mb-5" data-testid="wizard-stepper">
        {STEPS.map((s, i) => {
          const SIcon = s.icon;
          const active = i === stepIdx;
          const done = i < stepIdx;
          return (
            <div key={s.id} className="flex-1 flex items-center" data-testid={`wizard-step-${s.id}`}>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold transition-colors ${
                active ? 'bg-cyan-600 text-white'
                : done ? 'bg-emerald-500 text-white'
                : 'bg-gray-200 text-gray-500'
              }`}>
                {done ? <Check className="w-4 h-4" /> : <SIcon className="w-4 h-4" />}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-px mx-1 ${done ? 'bg-emerald-400' : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">
        Step {stepIdx + 1} of {STEPS.length} · {Step.label}
      </p>

      {/* Step body */}
      <Card>
        <CardContent className="p-5 space-y-4">
          {Step.id === 'role' && (
            <RoleStep form={form} template={template} onChange={changeRole} />
          )}
          {Step.id === 'metrics' && (
            <MetricsStep
              fields={numericFields}
              form={form}
              aiPrefilled={aiPrefilled}
              setField={setField}
              aiBusy={aiBusy}
              onTriggerExtract={runAiExtraction}
            />
          )}
          {Step.id === 'highlights' && (
            <HighlightsStep form={form} aiPrefilled={aiPrefilled} setField={setField} />
          )}
          {Step.id === 'photos' && (
            <PhotosStep form={form} setField={setField} bookingId={bookingId} />
          )}
          {Step.id === 'review' && (
            <ReviewStep form={form} template={template} numericFields={numericFields} />
          )}
        </CardContent>
      </Card>

      {/* Footer nav */}
      <div className="flex items-center justify-between mt-4">
        <Button
          variant="outline"
          onClick={stepIdx === 0 ? onClose : prev}
          disabled={saving}
          data-testid="wizard-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {stepIdx === 0 ? 'Close' : 'Back'}
        </Button>
        <div className="flex items-center gap-2">
          {stepIdx < STEPS.length - 1 ? (
            <Button onClick={next} disabled={saving} data-testid="wizard-next-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Next <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={submitFinal} disabled={saving} data-testid="wizard-submit-btn"
                    className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Submit campaign data
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────

const RoleStep = ({ form, template, onChange }) => (
  <div>
    <h3 className="text-sm font-semibold text-gray-900 mb-1">Confirm your role on this shift</h3>
    <p className="text-xs text-gray-500 mb-3">
      We&apos;ve suggested <Badge className="bg-cyan-100 text-cyan-700 mx-1">{template?.role_label}</Badge>
      based on the shift title. Change it if it doesn&apos;t fit.
    </p>
    <select
      value={form.role_category || 'event_staff'}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-11 border border-gray-200 rounded-lg px-3 text-sm"
      data-testid="wizard-role-select"
    >
      {(template?.all_role_categories || []).map((r) => (
        <option key={r.value} value={r.value}>{r.label}</option>
      ))}
    </select>
  </div>
);

const AiBadge = ({ visible }) => visible ? (
  <Badge className="ml-2 bg-amber-100 text-amber-700 border border-amber-200 text-[10px]" data-testid="wizard-ai-badge">
    <Sparkles className="w-3 h-3 mr-0.5" /> AI suggested · verify
  </Badge>
) : null;

const MetricsStep = ({ fields, form, aiPrefilled, setField, aiBusy, onTriggerExtract }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-gray-900">Performance metrics</h3>
      <Button variant="ghost" size="sm" onClick={onTriggerExtract} disabled={aiBusy} data-testid="wizard-trigger-ai">
        {aiBusy
          ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Parsing voice…</>
          : <><Mic className="w-3 h-3 mr-1" />Pull from voice recap</>}
      </Button>
    </div>
    <div className="space-y-3">
      {fields.map((f) => {
        const isRetail = f === 'retail_sales_cents';
        const val = form[f];
        return (
          <div key={f} data-testid={`wizard-field-${f}`}>
            <label className="text-xs font-medium text-gray-700 flex items-center mb-1">
              {FIELD_LABELS[f] || f}
              <AiBadge visible={!!aiPrefilled[f]} />
            </label>
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              placeholder={isRetail ? '$ 0' : '0'}
              value={val ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') return setField(f, null);
                const n = parseInt(raw, 10);
                if (!Number.isNaN(n)) setField(f, isRetail ? n * 100 : n);
              }}
              className={aiPrefilled[f] ? 'border-amber-300 bg-amber-50/30' : ''}
            />
          </div>
        );
      })}
      {fields.length === 0 && (
        <p className="text-xs text-gray-500 italic">No quantitative metrics for this role — skip ahead.</p>
      )}
    </div>
  </div>
);

const HighlightsStep = ({ form, aiPrefilled, setField }) => (
  <div>
    <h3 className="text-sm font-semibold text-gray-900 mb-1">Campaign highlights</h3>
    <p className="text-xs text-gray-500 mb-3">Short answers are fine. These power the client&apos;s campaign report.</p>
    <div className="space-y-3">
      {Object.entries(HIGHLIGHT_LABELS).map(([k, label]) => (
        <div key={k} data-testid={`wizard-highlight-${k}`}>
          <label className="text-xs font-medium text-gray-700 flex items-center mb-1">
            {label}
            <AiBadge visible={!!aiPrefilled[k]} />
          </label>
          <Textarea
            rows={2}
            value={form[k] || ''}
            onChange={(e) => setField(k, e.target.value)}
            placeholder={`e.g. ${PLACEHOLDERS[k]}`}
            className={aiPrefilled[k] ? 'border-amber-300 bg-amber-50/30' : ''}
          />
        </div>
      ))}
    </div>
  </div>
);

const PLACEHOLDERS = {
  biggest_success:         'Landed 5 qualified leads in the first hour',
  most_common_question:    'Is this product gluten-free?',
  most_common_objection:   'Too expensive compared to brand X',
  competitor_observations: 'Brand X was sampling next to us with smaller portions',
  recommendations:         'Add a second sampling station for peak hours',
  additional_observations: 'Foot traffic peaked between noon and 2 PM',
};

const PhotosStep = ({ form, setField, bookingId }) => {
  const [uploading, setUploading] = useState(null); // index being uploaded
  const [activeCategory, setActiveCategory] = useState('booth_setup');
  const photos = form.photos || [];

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(photos.length);
    const upFile = await downscaleImage(file);
    const fd = new FormData();
    fd.append('file', upFile);
    fd.append('category', activeCategory);
    try {
      const r = await api.post(`/shift-metrics/${bookingId}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Server returns the latest photo; refetch the row's photos to stay in sync
      const updated = [...photos, r.data.photo];
      setField('photos', updated);
      if (r.data.pending_sync) {
        toast.warning('Photo saved (queued)', {
          description:
            r.data.message ||
            "We're holding onto your photo while cloud storage recovers — no penalty applies.",
        });
      } else {
        toast.success(`Photo uploaded · ${PHOTO_CATEGORY_LABELS[activeCategory] || activeCategory}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      if (isNetworkError(err)) {
        await enqueueUpload({
          endpoint: `/shift-metrics/${bookingId}/photo`,
          file: upFile,
          fields: { category: activeCategory },
          description: 'shift metrics photo',
        });
        toast.info('Photo saved on this device', {
          description: 'No signal right now — it will upload automatically when you are back online. No penalty applies.',
        });
      } else {
        toast.error('Photo upload failed', { description: detail });
      }
    } finally {
      setUploading(null);
      e.target.value = '';
    }
  };

  const removePhoto = async (idx) => {
    try {
      await api.delete(`/shift-metrics/${bookingId}/photo/${idx}`);
      const next = photos.filter((_, i) => i !== idx);
      setField('photos', next);
    } catch (err) {
      toast.error('Could not remove photo');
    }
  };

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Photos</h3>
      <p className="text-xs text-gray-500 mb-2">Up to 10 photos · 10 MB each. Tag each one so reports can group them.</p>

      {/* Category picker */}
      <div className="mb-3 flex flex-wrap gap-1">
        {Object.entries(PHOTO_CATEGORY_LABELS).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setActiveCategory(k)}
            className={`text-[11px] px-2 py-1 rounded-full border ${
              activeCategory === k
                ? 'bg-cyan-600 text-white border-cyan-600'
                : 'bg-white text-gray-600 border-gray-200 hover:border-cyan-300'
            }`}
            data-testid={`wizard-photo-category-${k}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {Array.from({ length: 10 }).map((_, i) => {
          const ph = photos[i];
          return (
            <div
              key={i}
              className="aspect-square rounded-lg border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-400 relative overflow-hidden"
              data-testid={`wizard-photo-slot-${i}`}
            >
              {ph ? (
                <>
                  <img src={ph.url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] hover:bg-rose-600"
                    data-testid={`wizard-photo-remove-${i}`}
                  >×</button>
                  <Badge className="absolute bottom-1 left-1 bg-black/60 text-white text-[9px] border-0">
                    {PHOTO_CATEGORY_LABELS[ph.category] || ph.category}
                  </Badge>
                </>
              ) : uploading === i ? (
                <Loader2 className="w-5 h-5 animate-spin text-cyan-500" />
              ) : i === photos.length ? (
                <label className="cursor-pointer w-full h-full flex items-center justify-center" data-testid="wizard-photo-add">
                  <Camera className="w-5 h-5" />
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/heic"
                         className="hidden" onChange={onPick} />
                </label>
              ) : (
                <Camera className="w-5 h-5 opacity-40" />
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-gray-400 mt-2 italic">
        Tag a category first, then tap the next empty slot to upload. Reports will group photos by category.
      </p>
    </div>
  );
};

const PHOTO_CATEGORY_LABELS = {
  product_display:    'Product display',
  booth_setup:        'Booth setup',
  sampling_activity:  'Sampling activity',
  crowd_engagement:   'Crowd',
  team_photo:         'Team',
  before_after_setup: 'Before / after',
  other:              'Other',
};

const ReviewStep = ({ form, template, numericFields }) => {
  const enteredNumeric = numericFields.filter((f) => form[f] !== undefined && form[f] !== null && form[f] !== '');
  const enteredHighlights = Object.keys(HIGHLIGHT_LABELS).filter((k) => (form[k] || '').trim());
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Review and submit</h3>
      <p className="text-xs text-gray-500 mb-3">Confirm everything looks right.</p>
      <div className="text-xs space-y-1 mb-4">
        <p><span className="text-gray-500">Role:</span> <span className="font-medium">{template?.role_label}</span></p>
        <p><span className="text-gray-500">Metrics filled:</span> <span className="font-medium">{enteredNumeric.length} of {numericFields.length}</span></p>
        <p><span className="text-gray-500">Highlights filled:</span> <span className="font-medium">{enteredHighlights.length} of 6</span></p>
        <p><span className="text-gray-500">Photos uploaded:</span> <span className="font-medium">{(form.photos || []).length} of 10</span></p>
      </div>
      {enteredHighlights.length < 3 && (
        <div className="px-3 py-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
          Tip: filling in at least 3 highlights makes the client report meaningfully better.
        </div>
      )}
    </div>
  );
};

export default ShiftMetricsWizard;
